/**
 * Fluxo de caixa da empresa (trazido do sistema "VMM Fluxo de Caixa").
 * Lançamentos de entrada e saída com vencimento e baixa; saldo = saldo inicial + realizados.
 * Medições de contrato (a pagar e a receber) entram como lançamentos automáticos, sem digitação.
 */
import { addDays, monthsBetween } from './dates.js';
export const CASH_IN_CATEGORIES = ['Medição de obra', 'Projeto / consultoria', 'Fiscalização', 'ART / laudos / perícias', 'Adiantamento de cliente', 'Outras receitas'];
export const CASH_OUT_CATEGORIES = ['Folha e encargos', 'Pró-labore', 'Subempreiteiros', 'Materiais e ensaios', 'Aluguel de equipamentos', 'Software e licenças', 'Impostos', 'CREA / ARTs', 'Combustível e deslocamento', 'Escritório', 'Contabilidade', 'Outras despesas'];
export const CASH_FORMS = ['PIX', 'Boleto', 'TED / transferência', 'Cartão', 'Dinheiro', 'Cheque'];
/** Níveis de acesso ao caixa, por pessoa (só da empresa dona). */
export const CASH_ACCESS = { nenhum: 'Sem acesso', consulta: 'Consulta', lanca: 'Lança e dá baixa', admin: 'Administra' };
const ORDER = { nenhum: 0, consulta: 1, lanca: 2, admin: 3 };
export const cashCan = (a, need) => ORDER[a ?? 'nenhum'] >= ORDER[need];
/** Situação e data efetiva de cada lançamento: realizado pela baixa; em aberto vencido conta como amanhã na projeção. */
export function cashRows(entries, today) {
    return entries.map(e => {
        const real = !!e.paidDate;
        const state = real ? 'realizado' : e.dueDate < today ? 'vencido' : 'previsto';
        return { ...e, state, sign: e.type === 'entrada' ? 1 : -1, effective: real ? e.paidDate : e.dueDate < today ? today : e.dueDate, lateDays: state === 'vencido' ? Math.round((Date.parse(today) - Date.parse(e.dueDate)) / 864e5) : 0 };
    });
}
export const cashStateLabel = (r) => r.state === 'realizado' ? (r.type === 'entrada' ? 'Recebido' : 'Pago') : r.state === 'vencido' ? (r.type === 'entrada' ? 'Atrasado' : 'Vencido') : (r.type === 'entrada' ? 'A receber' : 'A pagar');
const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const sum = (rows, f) => r2(rows.filter(f).reduce((s, r) => s + r.amount, 0));
const monthEnd = (ym) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`; };
/** Lançamentos sem projeto: receitas e despesas da própria empresa (aluguel, contabilidade...). */
export const COMPANY = 'Empresa (sem projeto)';
/** Indicadores do painel para o mês escolhido (AAAA-MM). */
export function cashPanel(entries, opening, today, month) {
    const rows = cashRows(entries, today);
    const inM = (d) => d.slice(0, 7) === month;
    const balance = r2(opening + rows.filter(r => r.state === 'realizado').reduce((s, r) => s + r.sign * r.amount, 0));
    const end = monthEnd(month);
    const projectedMonthEnd = r2(opening + rows.filter(r => (r.state === 'realizado' ? r.paidDate : r.dueDate > today ? r.dueDate : addDays(today, 1)) <= end).reduce((s, r) => s + r.sign * r.amount, 0));
    const receivedMonth = sum(rows, r => r.type === 'entrada' && r.state === 'realizado' && inM(r.paidDate));
    const paidMonth = sum(rows, r => r.type === 'saida' && r.state === 'realizado' && inM(r.paidDate));
    const toReceiveMonth = sum(rows, r => r.type === 'entrada' && r.state !== 'realizado' && inM(r.effective));
    const toPayMonth = sum(rows, r => r.type === 'saida' && r.state !== 'realizado' && inM(r.effective));
    const cats = new Map();
    for (const r of rows)
        if (r.type === 'saida' && inM(r.effective))
            cats.set(r.category || 'Sem categoria', (cats.get(r.category || 'Sem categoria') ?? 0) + r.amount);
    const projs = new Map();
    for (const r of rows) {
        const k = r.projectLabel || COMPANY;
        const o = projs.get(k) ?? { inflow: 0, outflow: 0 };
        if (r.type === 'entrada')
            o.inflow += r.amount;
        else
            o.outflow += r.amount;
        projs.set(k, o);
    }
    const ym = (n) => { const [y, m] = month.split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + n, 1)); return d.toISOString().slice(0, 7); };
    const months = [-2, -1, 0, 1, 2, 3].map(ym).map(mo => ({ month: mo,
        inReal: sum(rows, r => r.type === 'entrada' && r.state === 'realizado' && r.effective.slice(0, 7) === mo), inForecast: sum(rows, r => r.type === 'entrada' && r.state !== 'realizado' && r.effective.slice(0, 7) === mo),
        outReal: sum(rows, r => r.type === 'saida' && r.state === 'realizado' && r.effective.slice(0, 7) === mo), outForecast: sum(rows, r => r.type === 'saida' && r.state !== 'realizado' && r.effective.slice(0, 7) === mo) }));
    // saldo diário: do início do mês anterior ao fim do 3º mês seguinte
    const start = `${ym(-1)}-01`;
    const stop = monthEnd(ym(3));
    const delta = new Map();
    let base = opening;
    for (const r of rows) {
        const d = r.state === 'realizado' ? r.paidDate : r.dueDate > today ? r.dueDate : addDays(today, 1);
        const v = r.sign * r.amount;
        if (d < start)
            base += v;
        else if (d <= stop)
            delta.set(d, (delta.get(d) ?? 0) + v);
    }
    const daily = [];
    let cur = base;
    for (let d = start; d <= stop; d = addDays(d, 1)) {
        cur += delta.get(d) ?? 0;
        daily.push({ date: d, balance: r2(cur), projected: d > today });
    }
    return {
        balance, projectedMonthEnd, receivedMonth, paidMonth, toReceiveMonth, toPayMonth, resultMonth: r2(receivedMonth - paidMonth), resultMonthWithForecast: r2(receivedMonth - paidMonth + toReceiveMonth - toPayMonth),
        openReceivable: sum(rows, r => r.type === 'entrada' && r.state !== 'realizado'), lateReceivable: sum(rows, r => r.type === 'entrada' && r.state === 'vencido'),
        openPayable: sum(rows, r => r.type === 'saida' && r.state !== 'realizado'), latePayable: sum(rows, r => r.type === 'saida' && r.state === 'vencido'),
        byCategory: [...cats].map(([category, amount]) => ({ category, amount: r2(amount) })).sort((a, b) => b.amount - a.amount),
        byProject: [...projs].map(([project, o]) => ({ project, inflow: r2(o.inflow), outflow: r2(o.outflow), result: r2(o.inflow - o.outflow) })).sort((a, b) => (a.project === COMPANY ? 1 : 0) - (b.project === COMPANY ? 1 : 0) || b.inflow - a.inflow || b.outflow - a.outflow),
        months, daily
    };
}
/** Parcelas mensais: mesmo dia do mês (ou o último dia, em meses mais curtos). */
export function installmentDates(first, n) {
    const [y, m, d] = first.split('-').map(Number);
    const out = [];
    for (let i = 0; i < Math.max(1, Math.min(60, n)); i++) {
        const last = new Date(Date.UTC(y, m - 1 + i + 1, 0)).getUTCDate();
        const dt = new Date(Date.UTC(y, m - 1 + i, Math.min(d, last)));
        out.push(dt.toISOString().slice(0, 10));
    }
    return out;
}
/** Lê o CSV exportado pelo sistema antigo (VMM Fluxo de Caixa). */
export function parseLegacyCashCsv(rows) {
    const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    const head = (rows[0] ?? []).map(norm);
    const col = (n) => head.indexOf(norm(n));
    const need = ['Tipo', 'Descrição', 'Valor', 'Vencimento'];
    const miss = need.filter(n => col(n) < 0);
    if (miss.length)
        return { entries: [], errors: [`O arquivo não tem as colunas ${miss.join(', ')}. Use o CSV exportado pelo fluxo de caixa.`] };
    const date = (s) => { const m = (s ?? '').trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : null; };
    const money = (s) => { let t = String(s ?? '').replace(/R\$|\s/g, ''); if (t.includes(','))
        t = t.replace(/\./g, '').replace(',', '.'); const n = Number(t); return Number.isFinite(n) ? r2(n) : NaN; };
    const get = (r, n) => col(n) >= 0 ? (r[col(n)] ?? '').trim() : '';
    const entries = [];
    const errors = [];
    rows.slice(1).forEach((r, i) => {
        if (!r.some(x => x.trim()))
            return;
        const t = norm(get(r, 'Tipo'));
        const type = t.startsWith('entr') ? 'entrada' : t.startsWith('sai') ? 'saida' : null;
        const due = date(get(r, 'Vencimento'));
        const amount = money(get(r, 'Valor'));
        const paid = get(r, 'Pagamento') ? date(get(r, 'Pagamento')) : null;
        if (!type || !due || !Number.isFinite(amount) || amount <= 0 || !get(r, 'Descrição') || (get(r, 'Pagamento') && !paid)) {
            errors.push(`Linha ${i + 2}: tipo, descrição, valor ou datas inválidos.`);
            return;
        }
        entries.push({ type, description: get(r, 'Descrição'), category: get(r, 'Categoria') || null, projectLabel: get(r, 'Obra/projeto') || null, party: get(r, 'Cliente/fornecedor') || null, amount, dueDate: due, paidDate: paid, method: get(r, 'Forma') || null, document: get(r, 'Documento') || null, source: 'manual' });
    });
    return { entries, errors };
}
export const cashMonths = (a, b) => monthsBetween(a, b);
//# sourceMappingURL=cash.js.map